import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDungeon,
  faScroll,
  faCoins,
  faStore,
  faUsers,
  faFistRaised,
  faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import styles from './Welcome.module.css';
import { useAuth } from '../../contexts/AuthContext';

interface SeasonInfo {
  id: number;
  status: string;
  startDate: string;
  championShowdownTournamentId: number | null;
}

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOffseason, setIsOffseason] = useState(false);
  const [nextSeasonStart, setNextSeasonStart] = useState<string | null>(null);
  const [championTournamentId, setChampionTournamentId] = useState<number | null>(null);

  useEffect(() => {
    const checkSeason = async () => {
      try {
        const [currentRes, seasonsRes] = await Promise.all([
          axios.get('/api/user/current-season', { withCredentials: true }),
          axios.get('/api/seasons', { withCredentials: true }),
        ]);

        const offSeasonPlayEnabled = currentRes.data.offSeasonPlayEnabled || false;
        const season = currentRes.data.season;
        const hasActiveSeason = season && season.status !== 'upcoming';

        if (!hasActiveSeason && !offSeasonPlayEnabled) {
          setIsOffseason(true);
          if (season?.status === 'upcoming') {
            setNextSeasonStart(season.startDate);
          }
          const seasons: SeasonInfo[] = seasonsRes.data;
          const lastCompleted = seasons.find(s => s.status === 'completed');
          if (lastCompleted?.championShowdownTournamentId) {
            setChampionTournamentId(lastCompleted.championShowdownTournamentId);
          }
        }
      } catch (err) {
        console.error('Error checking season:', err);
      }
    };
    checkSeason();
  }, []);

  const handleCreateMercenary = () => {
    navigate('/create-mercenary');
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  // Mirrors backend getNextOddMonthStart() — 1st of next odd month at 18:00 UTC
  const getNextSeasonStart = (): Date => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const oddMonths = [0, 2, 4, 6, 8, 10];
    for (const month of oddMonths) {
      const candidate = new Date(Date.UTC(year, month, 1, 18, 0, 0));
      if (candidate > now) return candidate;
    }
    return new Date(Date.UTC(year + 1, 0, 1, 18, 0, 0));
  };

  const formatDateUTC = (date: Date) =>
    date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        
        <h1 className={styles.title}>NJORDHEIM</h1>
        
        {user && (
          <p className={styles.greeting}>
            Greetings, <span className={styles.username}>{user.username}</span>.
          </p>
        )}
        
        <div className={styles.description}>
          <p>
            Welcome to the lands of Njordheim. Here you will command mercenaries,
            forge your legacy in blood and steel, and compete for dominance in the seasonal rankings.
          </p>
        </div>
          
        <div className={styles.featuresGrid}>
            <div className={styles.featureItem}>
              <div className={styles.iconWrapper}><FontAwesomeIcon icon={faFistRaised} /></div>
              <div className={styles.featureText}>
                <h3>Mercenary School</h3>
                <p>Train your recruits in combat to gain experience and hone their skills.</p>
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.iconWrapper}><FontAwesomeIcon icon={faCoins} /></div>
              <div className={styles.featureText}>
                <h3>Economic Strategy</h3>
                <p>Work in districts to earn gold or risk your life for greater rewards.</p>
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.iconWrapper}><FontAwesomeIcon icon={faStore} /></div>
              <div className={styles.featureText}>
                <h3>The Market</h3>
                <p>Visit Durin to equip your champions with weapons, armor, and potions.</p>
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.iconWrapper}><FontAwesomeIcon icon={faDungeon} /></div>
              <div className={styles.featureText}>
                <h3>Adventures</h3>
                <p>Embark on dangerous quests to uncover artifacts and face legendary beasts.</p>
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.iconWrapper}><FontAwesomeIcon icon={faUsers} /></div>
              <div className={styles.featureText}>
                <h3>Guild Warfare</h3>
                <p>Form alliances and wage war against rival clans for territory.</p>
              </div>
            </div>

            <div className={styles.featureItem}>
              <div className={styles.iconWrapper}><FontAwesomeIcon icon={faScroll} /></div>
              <div className={styles.featureText}>
                <h3>Seasonal Legacy</h3>
                <p>Compete in ranked seasons. Only the strongest are etched in the Hall of Honor.</p>
              </div>
            </div>
          </div>

        <div className={styles.actions}>
          {isOffseason ? (
            <>
              <div className={styles.offseasonBanner}>
                <FontAwesomeIcon icon={faTrophy} className={styles.offseasonIcon} />
                <p className={styles.offseasonTitle}>Season Completed!</p>
                {nextSeasonStart ? (
                  <p className={styles.offseasonDate}>
                    Next season begins: <strong>{formatDate(nextSeasonStart)}</strong>
                  </p>
                ) : (
                  <p className={styles.offseasonDate}>
                    Next season begins: <strong>{formatDateUTC(getNextSeasonStart())}</strong> at 18:00 UTC
                  </p>
                )}
              </div>
              {championTournamentId && (
                <button
                  className={styles.primaryButton}
                  onClick={() => navigate(`/tournament/${championTournamentId}`)}
                >
                  View Championship Showdown Results
                </button>
              )}
            </>
          ) : (
            <button
              className={styles.primaryButton}
              onClick={handleCreateMercenary}
            >
              Create Mercenary
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Welcome;
